import React, { useEffect, useState } from 'react';
import DataTable from '../../components/UI/table';
import { styles } from './index.sass';
import { Button, Modal, Form, Input, InputNumber, DatePicker, Select, Upload, message, Table } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { CheckCircleTwoTone, ClockCircleTwoTone } from '@ant-design/icons';
import { createContractorSession, getContractorSessions } from '../../api/contractorSession';
import { getClients } from '../../api';
import { uploadInvoice } from '../../api/extractFile';
import moment from 'moment';

export const ContractorSessions = () => {
    const [loading, setLoading] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [clients, setClients] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [selectedDate, setSelectedDate] = useState({ start: null, end: null });
    const [selectedContractor, setSelectedContractor] = useState(null);
    const [invoiceUploading, setInvoiceUploading] = useState(false);
    const [invoiceEntries, setInvoiceEntries] = useState([]);
    const [invoiceReviewOpen, setInvoiceReviewOpen] = useState(false);
    const [invoiceSaving, setInvoiceSaving] = useState(false);

    const columns = [
        {
            title: 'Status', key: 'is_approved', render: (record) => {
                const matched = record.appointment_id ? true : false;
                let is_partial_matched = false;
                if (matched) {
                    const original_appointment = JSON.parse(record.original_appointment);
                    is_partial_matched = !(original_appointment.duration === record.duration);
                }
                return (
                    (record.appointment_id && !is_partial_matched) ?
                        <CheckCircleTwoTone twoToneColor="#52c41a" title="Matched" /> :
                        (is_partial_matched ? <ClockCircleTwoTone twoToneColor="#faad14" title="Partially Matched" /> : <ClockCircleTwoTone twoToneColor="red" title="Unmatched" />)
                )
            }
        },
        { title: 'Contractor Name', dataIndex: 'contractor_name', key: 'contractor_name' },
        {
            title: 'Client', dataIndex: 'client_id', key: 'client_id', render: (person_id) => {
                console.log('person_id', person_id, clients);
                const client = clients.find(client => client.id === person_id)
                return client ? client.full_name : "";
            }
        },
        { title: 'Duration', dataIndex: 'duration', key: 'duration' },
        { title: 'Date', dataIndex: 'date', key: 'date' },
        { title: 'Pay', dataIndex: 'pay', key: 'pay' },
        { title: 'Original Appointment', dataIndex: 'original_appointment', key: 'original_appointment' },
    ];

    const fetchContractorSessions = async () => {
        setLoading(true);
        const sessions = await getContractorSessions({
            contractor_name: selectedContractor,
            start_date: selectedDate.start ? selectedDate.start.format('YYYY-MM-DD') : undefined,
            end_date: selectedDate.end ? selectedDate.end.format('YYYY-MM-DD') : undefined
        });
        if (clients.length === 0) {
            const clientsData = await getClients();
            setClients(clientsData.clients);
        }
        setSessions(sessions);
        setLoading(false);
    }

    const onClickCreateContractorSession = async (data) => {
        setLoading(true);
        await createContractorSession(data);
        setLoading(false);
        setSelectedSession(null);
        fetchContractorSessions();
    }

    useEffect(() => {
        fetchContractorSessions();
    }, []);

    useEffect(() => {
        fetchContractorSessions()
    }, [selectedDate, selectedContractor]);

    const handleChangeDate = (dates) => {
        setSelectedDate({
            start: dates ? dates[0] : null,
            end: dates ? dates[1] : null
        });
    }

    const beforeUploadInvoice = (file) => {
        const isPdf = file.type === 'application/pdf' || (file.name || '').toLowerCase().endsWith('.pdf');
        if (!isPdf) {
            message.error('Please upload a PDF invoice file only.');
            return false;
        }
        return true;
    };

    const onUploadInvoice = async ({ file, onSuccess, onError }) => {
        try {
            setInvoiceUploading(true);
            const response = await uploadInvoice(file);
            const extracted = getExtractedEntries(response);
            if (!extracted.length) {
                message.warning('Invoice uploaded, but no entries were extracted.');
            } else {
                setInvoiceEntries(
                    extracted.map((entry, index) => ({
                        ...entry,
                        _rowKey: entry.id || entry.entry_id || `entry-${index}`
                    }))
                );
                setInvoiceReviewOpen(true);
                message.success('Invoice uploaded. Please review and submit entries.');
            }
            onSuccess && onSuccess({}, file);
        } catch (error) {
            message.error('Invoice upload failed. Please try again.');
            onError && onError(error);
        } finally {
            setInvoiceUploading(false);
        }
    };

    const getExtractedEntries = (response) => {
        const candidateLists = [
            response && response.entries,
            response && response.invoice_entries,
            response && response.items,
            response && response.data && response.data.entries,
            response && response.data && response.data.invoice_entries,
            response && response.data && response.data.items,
            Array.isArray(response) ? response : null
        ];

        const found = candidateLists.find((list) => Array.isArray(list));
        return found || [];
    };

    const onChangeInvoiceField = (index, field, value) => {
        setInvoiceEntries((prev) => {
            const next = [...prev];
            next[index] = Object.assign({}, next[index], { [field]: value });
            return next;
        });
    };

    const onSubmitInvoiceEntries = async () => {
        try {
            setInvoiceSaving(true);
            let createdCount = 0;
            for (const entry of invoiceEntries) {
                const payload = buildSessionPayloadFromEntry(entry);
                await createContractorSession(payload);
                createdCount += 1;
            }
            message.success(`${createdCount} invoice entr${createdCount === 1 ? 'y' : 'ies'} submitted successfully.`);
            setInvoiceReviewOpen(false);
            setInvoiceEntries([]);
            fetchContractorSessions();
        } catch (error) {
            message.error('Failed to save invoice entries. Please try again.');
        } finally {
            setInvoiceSaving(false);
        }
    };

    const buildSessionPayloadFromEntry = (entry) => {
        const clientName = entry.client_name || entry.client_full_name || entry.client;
        const matchedClient = clientName
            ? (clients || []).find((c) => (c.full_name || '').toLowerCase() === String(clientName).toLowerCase())
            : null;

        const normalizedDate = entry.date || entry.session_date || entry.invoice_date;

        return {
            contractor_name: entry.contractor_name || entry.contractor || '',
            client_id: entry.client_id || (matchedClient ? matchedClient.id : undefined),
            duration: Number(entry.duration || entry.duration_minutes || entry.minutes || 0),
            date: normalizedDate,
            pay: Number(entry.pay || entry.amount || entry.total || 0)
        };
    };

    const entryKeys = invoiceEntries.length
        ? Object.keys(invoiceEntries[0]).filter((key) => key !== '_rowKey')
        : [];

    const isDateField = (field) => /date/.test((field || '').toLowerCase());
    const isClientNameField = (field) => {
        const normalized = (field || '').toLowerCase();
        return (
            normalized === 'client' ||
            normalized === 'client_name' ||
            normalized === 'client_full_name' ||
            normalized.includes('client_name') ||
            normalized.includes('name_client')
        );
    };

    const invoiceColumns = entryKeys.map((key) => ({
        title: key.replace(/_/g, ' ').replace(/\b\w/g, (s) => s.toUpperCase()),
        dataIndex: key,
        key,
        render: (value, record, index) => {
            if (isDateField(key)) {
                const dateValue = value && moment(value).isValid() ? moment(value) : null;
                return (
                    <DatePicker
                        style={{ width: '100%' }}
                        value={dateValue}
                        onChange={(_, dateString) => onChangeInvoiceField(index, key, dateString || '')}
                    />
                );
            }
            if (isClientNameField(key)) {
                return (
                    <Select
                        showSearch
                        allowClear
                        value={value || undefined}
                        style={{ width: '100%' }}
                        placeholder="Select client"
                        optionFilterProp="children"
                        onChange={(v) => onChangeInvoiceField(index, key, v || '')}
                        filterOption={(input, option) =>
                            (option && option.children ? option.children : '')
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        {(clients || []).map((client) => (
                            <Select.Option key={client.id} value={client.full_name}>
                                {client.full_name}
                            </Select.Option>
                        ))}
                    </Select>
                );
            }
            if (typeof value === 'number') {
                return (
                    <InputNumber
                        style={{ width: '100%' }}
                        value={value}
                        onChange={(v) => onChangeInvoiceField(index, key, v)}
                    />
                );
            }
            if (typeof value === 'boolean') {
                return (
                    <Select
                        value={String(value)}
                        style={{ width: '100%' }}
                        onChange={(v) => onChangeInvoiceField(index, key, v === 'true')}
                    >
                        <Select.Option value="true">True</Select.Option>
                        <Select.Option value="false">False</Select.Option>
                    </Select>
                );
            }
            return (
                <Input
                    value={value === undefined || value === null ? '' : String(value)}
                    onChange={(e) => onChangeInvoiceField(index, key, e.target.value)}
                />
            );
        }
    }));

    return (
        <div className={styles}>
            <div className='container'>
                <div className='flex'>
                    <h2>Contractor Sessions</h2>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Upload
                            accept="application/pdf,.pdf"
                            showUploadList={false}
                            beforeUpload={beforeUploadInvoice}
                            customRequest={onUploadInvoice}
                            disabled={invoiceUploading}
                        >
                            <Button type="default" loading={invoiceUploading}>
                                Upload Invoice PDF
                            </Button>
                        </Upload>
                        <Button type="default" onClick={() => setSelectedSession({})}>
                            <PlusOutlined />
                        </Button>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 16, margin: '16px 0' }}>
                    <DatePicker.RangePicker
                        placeholder={['Start Date', 'End Date']}
                        value={[
                            selectedDate.start ? moment(selectedDate.start) : null,
                            selectedDate.end ? moment(selectedDate.end) : null
                        ]}
                        onChange={handleChangeDate}
                    />
                    <Select
                        showSearch
                        allowClear
                        placeholder="Filter by Contractor Name"
                        style={{ width: 220 }}
                        optionFilterProp="children"
                        value={selectedContractor || undefined}
                        onChange={value => setSelectedContractor(value)}
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        {[...new Set((sessions || []).map(s => s.contractor_name))].map(name => (
                            <Select.Option key={name} value={name}>{name}</Select.Option>
                        ))}
                    </Select>
                </div>
                <div>
                    <DataTable
                        columns={columns || []}
                        dataSource={sessions || []}
                        loading={loading}
                        hidePagination={true}
                    />
                </div>
                <Modal
                    open={selectedSession !== null}
                    onCancel={() => setSelectedSession(null)}
                    footer={null}
                >
                    <div>
                        <div style={{ fontWeight: 600, marginBottom: 12 }}>Create Contractor Session</div>
                        <Form
                            layout="vertical"
                            initialValues={{
                                contractor_name: selectedSession && selectedSession.contractor_name ? selectedSession.contractor_name : '',
                                client_id: selectedSession && selectedSession.client_id ? selectedSession.client_id : '',
                                duration: selectedSession && selectedSession.duration ? Number(selectedSession.duration) : '',
                                date: selectedSession && selectedSession.date ? selectedSession.date : null,
                                pay: selectedSession && selectedSession.pay ? Number(selectedSession.pay) : '',
                            }}
                            onFinish={(data) => onClickCreateContractorSession(data)}
                        >
                            <Form.Item label="Contractor Name" name="contractor_name" rules={[{ required: true, message: 'Please enter contractor name' }]}>
                                <Input
                                    value={selectedSession && selectedSession.contractor_name ? selectedSession.contractor_name : ''}
                                    onChange={e => setSelectedSession(Object.assign({}, selectedSession, { contractor_name: e.target.value }))}
                                />
                            </Form.Item>
                            <Form.Item label="Client" name="client_id" rules={[{ required: true, message: 'Please select a person' }]}>
                                <Select
                                    showSearch
                                    placeholder="Select a client"
                                    optionFilterProp="children"
                                    value={selectedSession && selectedSession.client_id ? selectedSession.client_id : undefined}
                                    onChange={value => setSelectedSession(Object.assign({}, selectedSession, { client_id: value }))}
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {(clients || []).map(client => (
                                        <Select.Option key={client.id} value={client.id}>
                                            {client.full_name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item label="Duration (minutes)" name="duration" rules={[{ required: true, message: 'Please enter duration' }]}>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    value={selectedSession && selectedSession.duration ? Number(selectedSession.duration) : ''}
                                    onChange={value => setSelectedSession(Object.assign({}, selectedSession, { duration: value }))}
                                />
                            </Form.Item>
                            <Form.Item label="Date" name="date" rules={[{ required: true, message: 'Please select date' }]}>
                                <DatePicker
                                    style={{ width: '100%' }}
                                    value={selectedSession && selectedSession.date ? selectedSession.date : null}
                                    onChange={(_, dateString) => setSelectedSession(Object.assign({}, selectedSession, { date: dateString }))}
                                />
                            </Form.Item>
                            <Form.Item label="Pay ($)" name="pay" rules={[{ required: true, message: 'Please enter pay' }]}>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    value={selectedSession && selectedSession.pay ? Number(selectedSession.pay) : ''}
                                    onChange={value => setSelectedSession(Object.assign({}, selectedSession, { pay: value }))}
                                />
                            </Form.Item>
                            <Form.Item>
                                <Button type="primary" htmlType="submit" loading={loading}>
                                    Submit
                                </Button>
                            </Form.Item>
                        </Form>
                    </div>
                </Modal>
                <Modal
                    width={1000}
                    open={invoiceReviewOpen}
                    title="Review Invoice Entries"
                    onCancel={() => setInvoiceReviewOpen(false)}
                    onOk={onSubmitInvoiceEntries}
                    okText="Submit Entries"
                    confirmLoading={invoiceSaving}
                >
                    <div style={{ marginBottom: 12, color: '#666' }}>
                        Review and edit extracted invoice entries before saving.
                    </div>
                    <Table
                        rowKey="_rowKey"
                        columns={invoiceColumns}
                        dataSource={invoiceEntries}
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                    />
                </Modal>
            </div>
        </div >
    )
}